import React, { useEffect, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  BALANCE,
  CONSOLIDATED_ENTITY,
  CURRENCY_LIST,
  LOCAL_CURRENCY,
} from "../../../utils/constants";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import useStyles from "./BalanceSheet.styles";
import BalanceSheetExport from "./BalanceSheetExport";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import {
  prepareDataForBalanceSheet,
  flattenBalanceSheetData,
  getMappedBusinessEntityIDs,
} from "../../../utils/utils";

const mapEntityID = (fxData) => ({
  id: fxData?.entityID,
  name: fxData?.entityID,
});

const mapCurrency = (currency) => ({
  id: currency,
  name: currency,
});

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const BalanceSheetFiltersAndReports = () => {
  const { classes } = useStyles();
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    selectedEntityID,
    setSelectedEntityID,
    localCurrency,
    setLocalCurrency,
    selectedCurrency,
    setSelectedCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    setSelectedMonthEnd,
    selectedMonthEnd,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,

    balanceSheetData,
    setBalanceSheetData,
    balanceSheetDataLoading,
    balanceSheetDataError,

    fetchBalanceSheetResultReport,

    multiledgerFXData,
    multiledgerFXDataLoading,

    fetchMultiledgerFXReport,
  } = useHedgeRatioContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const updatedMultiledgerFXData = [
    ...(multiledgerFXData?.length ? multiledgerFXData : []),
    ...(multiledgerFXData?.length
      ? [
          {
            businessEntityID: null,
            entityID: CONSOLIDATED_ENTITY,
            localCurrency: null,
            reportingCurrency: null,
            monthEnd: null,
            fxRate: null,
          },
        ]
      : []),
  ];

  const mappedEntityIDs = [
    ...(updatedMultiledgerFXData || []).map(mapEntityID),
  ];

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    if (
      selectedEntityID !== CONSOLIDATED_ENTITY &&
      selectedCurrency === LOCAL_CURRENCY
    ) {
      const { localCurrency: localCurrencyFromMultiledgerFXData } =
        (multiledgerFXData || [])?.find(
          (data) => data.entityID === selectedEntityID,
        ) || {};
      setLocalCurrency(localCurrencyFromMultiledgerFXData);
    } else {
      setLocalCurrency(currency);
    }
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
    selectedCurrency,
    selectedEntityID,
    multiledgerFXData,
  ]);

  useEffect(() => {
    if (selectedModelDatabase) {
      const modelObject = modelDatabases?.find((model) => {
        return model?.modelName === selectedModelDatabase;
      });
      const monthEndFromModelDatabase = modelObject?.monthEnd
        ? dayjs(modelObject.monthEnd?.replace("Z", ""))
        : null;
      setSelectedMonthEnd(monthEndFromModelDatabase);
    }
  }, [modelDatabases, selectedModelDatabase, setSelectedMonthEnd]);

  const currencies = useMemo(() => {
    if (
      !selectedBusinessEntityID ||
      !selectedEntityID ||
      !selectedModelDatabase
    ) {
      return [];
    }

    return (CURRENCY_LIST || []).map((currency) => mapCurrency(currency));
  }, [selectedBusinessEntityID, selectedEntityID, selectedModelDatabase]);

  useEffect(() => {
    if (selectedBusinessEntityID && selectedMonthEnd) {
      fetchMultiledgerFXReport();
    }
  }, [selectedBusinessEntityID, selectedMonthEnd, fetchMultiledgerFXReport]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const handleSubmitBtn = () => {
    if (
      selectedBusinessEntityID &&
      selectedEntityID &&
      selectedModelDatabase &&
      selectedCurrency
    ) {
      fetchBalanceSheetResultReport(BALANCE);
    } else {
      setBalanceSheetData([]);
    }
  };

  const dynamicColumns =
    balanceSheetData &&
    balanceSheetData?.length > 0 &&
    balanceSheetData?.[0]?.datas
      ? Object.keys(balanceSheetData?.[0]?.datas)?.map((key) => {
          return {
            id: key,
            label: key,
            minWidth: 90,
            align: "right",
          };
        })
      : [];

  const columns = [
    {
      id: "reportingAccountName",
      label: "Reporting Account Name",
      minWidth: 200,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const exportColumns = [
    { id: "level", label: "Account Level", minWidth: 60, align: "left" },
    {
      id: "formattedReportingAccountName",
      label: "Formatted Reporting Account Name",
      minWidth: 200,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const preprocessedData = useMemo(() => {
    if (!balanceSheetData || balanceSheetData.length === 0) {
      return [];
    }

    const divisor = Number(selectedFigureDivisor) || 1;
    const decimals =
      selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
        ? Number(selectedDecimalsIn)
        : null;

    return prepareDataForBalanceSheet(balanceSheetData, {
      divisor,
      decimals,
      showChildren: true,
      formatValues: true,
    });
  }, [balanceSheetData, selectedFigureDivisor, selectedDecimalsIn]);

  const preprocessedDataExport = useMemo(() => {
    if (!balanceSheetData || balanceSheetData.length === 0) {
      return [];
    }

    const treeData = prepareDataForBalanceSheet(balanceSheetData, {
      divisor: 1,
      decimals: null,
      showChildren: true,
      formatValues: false,
    });

    return flattenBalanceSheetData(treeData);
  }, [balanceSheetData]);

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the Balance Sheet Report (Modeler View) for the
        selected filters.
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"entity-id"}>Entity ID</InputLabel>
          <Autocomplete
            options={mappedEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedEntityIDs.find((item) => item.id === selectedEntityID) ||
              null
            }
            onChange={(_, newValue) => {
              setSelectedEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              multiledgerFXDataLoading
            }
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Entity ID"
                size="small"
              />
            )}
            sx={{ width: 200 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"currency"}>Currency</InputLabel>
          <Autocomplete
            options={currencies}
            getOptionLabel={(option) => option?.name || ""}
            value={
              currencies.find((item) => item.id === selectedCurrency) || null
            }
            onChange={(_, newValue) => {
              setSelectedCurrency(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              multiledgerFXDataLoading
            }
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Currency"
                size="small"
              />
            )}
            sx={{ width: 200 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrFiltersDataLoading ||
              balanceSheetDataLoading ||
              multiledgerFXDataLoading ||
              !selectedBusinessEntityID ||
              !selectedModelDatabase ||
              !selectedEntityID ||
              !selectedCurrency
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              balanceSheetDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              balanceSheetDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.balanceSheetExportBtn}>
            <BalanceSheetExport
              columns={exportColumns}
              datas={preprocessedDataExport}
              disabled={balanceSheetDataLoading || !balanceSheetData?.length}
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!balanceSheetDataLoading && balanceSheetDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!balanceSheetDataLoading && !balanceSheetData) ||
            (!balanceSheetDataLoading && !balanceSheetData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <ColumnGroupingTable
              columns={columns}
              datas={preprocessedData}
              loading={balanceSheetDataLoading}
              tableHeight={410}
              freezeParentColumnCount={1}
              applyThousandsSeparator={true}
              disablePagination
            />
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default BalanceSheetFiltersAndReports;
